import type { JSX } from "react";
import { useLayoutEffect, useRef } from "react";
import {
  AskBarDictationStatus,
  AskBarMic,
  useDictationInto,
} from "@/components/ui/DictationButton/AskBarMic";
import { cn } from "@/lib/utils";

const MAX_HEIGHT = 240;

/**
 * A text field for what the clinician dictates (a letter or a note), with
 * the Ask bar's mic: typed or dictated, a second dictation adds onto the
 * end. v3.2 tokens, light and dark.
 */
export const DictatedField = ({
  id,
  value,
  onChange,
  placeholder,
  maxLength,
  invalid = false,
  describedBy,
  minRows = 4,
  micLabel,
  testId,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  maxLength: number;
  invalid?: boolean;
  describedBy?: string;
  minRows?: number;
  micLabel: { start: string; stop: string };
  testId: string;
}): JSX.Element => {
  const field = useRef<HTMLTextAreaElement>(null);
  const { dictation, toggle } = useDictationInto(value, onChange);
  const dictating = dictation.status !== "idle";
  const listening = dictation.status === "listening";

  // Grows with the text, then scrolls.
  // biome-ignore lint/correctness/useExhaustiveDependencies: measured per value.
  useLayoutEffect(() => {
    const element = field.current;
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${Math.min(element.scrollHeight, MAX_HEIGHT)}px`;
  }, [value]);

  return (
    <div className="flex flex-col gap-2">
      <div
        className={cn(
          "rounded-[14px] bg-v3-surface-2 shadow-v3-hairline transition-shadow",
          listening
            ? "ring-[1.5px] ring-[color-mix(in_srgb,var(--v3-rec)_45%,transparent)]"
            : invalid
              ? "ring-[1.5px] ring-v3-rec"
              : "focus-within:bg-v3-panel focus-within:ring-2 focus-within:ring-v3-brand-ring",
        )}
        data-testid={testId}
      >
        <textarea
          ref={field}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          className="block w-full resize-none border-0 bg-transparent px-3 pt-2.5 pb-1 font-v3 text-[16px] leading-[22px] text-v3-text outline-hidden placeholder:text-v3-text-3 sm:text-[14px]"
          data-testid={`${testId}-input`}
          id={id}
          maxLength={maxLength}
          placeholder={placeholder}
          readOnly={dictating}
          rows={minRows}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
        <div className="flex items-center justify-end gap-1 px-2 pb-2">
          <AskBarMic
            dictation={dictation}
            label={micLabel}
            testId={`${testId}-mic`}
            onToggle={toggle}
          />
        </div>
      </div>
      <AskBarDictationStatus dictation={dictation} />
    </div>
  );
};
